import React from 'react';
import type { EditorMode } from '../../../../types/fingeringEditor';

export const SVG_WIDTH = 595;
export const SVG_HEIGHT = 842;

interface EditorSvgCanvasProps {
  // refs
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  svgWrapperRef: React.RefObject<HTMLDivElement | null>;
  svgRef: React.RefObject<SVGSVGElement | null>;
  // view state
  zoom: number;
  pan: { x: number; y: number };
  zoomOrigin: { x: number; y: number };
  isPanDragging: boolean;
  dragBadgeId: number | null;
  // mode state
  addMode: boolean;
  editorMode: EditorMode;
  isMobile: boolean;
  // touch / pointer handlers
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
  onSvgClick: (e: React.MouseEvent<SVGSVGElement>) => void;
  onSvgPointerDown: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerMove: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerUp: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerLeave: () => void;
  onPointerCancel: (e: React.PointerEvent<SVGSVGElement>) => void;
  // SVG 内の描画は children で受取 (PDF image / measureLines / pageBadges など)
  children: React.ReactNode;
}

/**
 * 編集画面の SVG キャンバス + スクロール / ピンチズーム container。
 * FingeringEditorPage から issue #17 Phase 2.6 で抽出。
 *
 * SVG 内の <rect>/<image>/<line>/<text>/<BadgeSvg> は children で受け取り、 当 component は
 * container, transform, cursor, ハンドラ配線だけを担当する。
 */
const EditorSvgCanvas: React.FC<EditorSvgCanvasProps> = ({
  scrollContainerRef, svgWrapperRef, svgRef,
  zoom, pan, zoomOrigin, isPanDragging, dragBadgeId,
  addMode, editorMode, isMobile,
  onTouchStart, onTouchEnd,
  onSvgClick, onSvgPointerDown, onPointerMove, onPointerUp, onPointerLeave, onPointerCancel,
  children,
}) => {
  const cursor = isPanDragging
    ? 'grabbing'
    : dragBadgeId
      ? 'grabbing'
      : addMode
        ? 'crosshair'
        : editorMode === 'edit' && zoom > 1 && !isMobile
          ? 'grab'
          : 'default';

  // zoom=1 + edit ではブラウザネイティブ縦スクロールに譲る。 それ以外は drag/pan 用に touch を奪う。
  // svgWrapper / svg で同一値を使うことで「片方乖離」 リスクを構造的に排除。
  const lockScroll = zoom > 1 || editorMode === 'move';
  const touchActionValue = lockScroll ? 'none' : 'auto';
  // scrollContainer は zoom=1 + edit のときページ縦スクロールのみ許可 (pinch zoom は禁止)。
  const scrollContainerTouchAction = lockScroll ? 'none' : 'pan-y';

  return (
    <div
      ref={scrollContainerRef}
      data-testid="editor-scroll-container"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className={`flex-1 flex justify-center items-start bg-slate-900 relative overscroll-none ${lockScroll ? 'overflow-hidden' : 'overflow-auto'}`}
      style={{ touchAction: scrollContainerTouchAction }}
    >
      <div data-testid="editor-svg-outer" className="w-full max-w-[800px] p-2 md:p-6">
        <div
          ref={svgWrapperRef}
          data-testid="editor-svg-wrapper"
          className="bg-amber-50 rounded shadow-2xl shadow-black/50 relative"
          style={{
            touchAction: touchActionValue,
            transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
            transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
            transition: 'none',
            cursor,
          }}
        >
          <svg
            ref={svgRef}
            data-testid="editor-svg-canvas"
            viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
            className="w-full block"
            style={{ touchAction: touchActionValue }}
            onClick={onSvgClick}
            onPointerDown={onSvgPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerLeave}
            onPointerCancel={onPointerCancel}
          >
            {children}
          </svg>
        </div>
      </div>
    </div>
  );
};

export default EditorSvgCanvas;
